import Dropzone, { DropzoneOptions } from 'dropzone';
import { useState, useEffect, useMemo } from 'react';
import { ExtendedDropzoneFile } from 'v2/models';
import { useMasterOrganization } from 'v2/queries';
import { s3Uploader } from 'v2/utils/s3Uploader';
import { baseDropzoneOptions, BulkFileUploadMessages } from '../constants';

function getPendingFiles(dropzone: Dropzone) {
  const successfulFiles = dropzone.getFilesWithStatus(Dropzone.SUCCESS);

  return dropzone.files.length - successfulFiles.length;
}

function getFilesData(dropzone: Dropzone) {
  return dropzone.files.map((file) => file.fileId);
}

type UseFilesDropzoneProps = {
  dropzoneRef: React.RefObject<HTMLElement>;
  onError?: () => void;
  onSuccess?: (data: string) => void;
  maxAllowedBulkUploadedFiles?: number;
  onFileAdded?: (file: ExtendedDropzoneFile) => void;
  onFileRemoved?: (file: ExtendedDropzoneFile) => void;
};

export function useFilesDropzone({
  dropzoneRef,
  onSuccess,
  onError,
  onFileAdded,
  onFileRemoved,
}: UseFilesDropzoneProps) {
  const s3UploadHelper = useMemo(() => new s3Uploader(), []);

  /**
   * Files computed by a diff between the local state and the dropzone state.
   */
  const [localFiles, setLocalFiles] = useState<ExtendedDropzoneFile[]>([]);

  const { data: masterOrganization, isLoading: isLoadingMasterOrganization } =
    useMasterOrganization();
  const maxAllowedBulkUploadedFiles =
    masterOrganization?.max_allowed_bulk_uploaded_files ?? 0;

  const [dropzone, setDropzone] = useState<Dropzone | undefined>();

  /**
   * Configure dropzone plugin effect.
   */
  useEffect(() => {
    if (!dropzoneRef.current || dropzone || isLoadingMasterOrganization) return;

    const dropzoneOptions: DropzoneOptions = {
      ...baseDropzoneOptions,
      previewTemplate: '',
      url: (files, dataBlocks) =>
        s3UploadHelper.getUploadUrl(
          files[0].fileId ?? '',
          dataBlocks[0].chunkIndex + 1
        ),
      maxFiles: maxAllowedBulkUploadedFiles,
      accept: async (file, done) => {
        if (file.size === 0) {
          done(BulkFileUploadMessages.EMPTY_FILE);
          return;
        }
        done();
        const fileId = await s3UploadHelper.getFileUrls(file);
        file.fileId = fileId;
        dropzoneInstance.processFile(file);
      },
    };

    const dropzoneInstance = new Dropzone(dropzoneRef.current, dropzoneOptions);
    setDropzone(dropzoneInstance);

    return () => {
      if (dropzoneInstance) dropzoneInstance.destroy();
    };
  }, [dropzoneRef, isLoadingMasterOrganization]);

  /**
   * Setup dropzone listeners.
   */
  if (dropzone) {
    dropzone.off('removedfile');
    dropzone.on('removedfile', (file) => {
      onFileRemoved?.(file);
      setLocalFiles(localFiles.filter((localFile) => localFile !== file));
      validateSucceededFiles();
    });

    dropzone.off('addedfile');
    dropzone.on('addedfile', (file) => {
      onFileAdded?.(file);
      setLocalFiles([...localFiles, file]);
    });

    dropzone.off('error');
    dropzone.on('error', async (file: ExtendedDropzoneFile, error) => {
      const errorMessage = typeof error === 'string' ? error : error.message;
      file.error = errorMessage;
      setLocalFiles([...localFiles]);

      if (file.previewElement) {
        const retryElement = file.previewElement.querySelector('.dz-retry');
        if (errorMessage === BulkFileUploadMessages.FAILED_UPLOAD) {
          retryElement?.classList.remove('hidden');
          retryElement?.addEventListener('click', () => {
            dropzone.removeFile(file);
            dropzone.addFile(file);
          });
        }
      }
    });

    dropzone.off('uploadprogress');
    dropzone.on('uploadprogress', () => setLocalFiles([...localFiles]));

    const validateSucceededFiles = () => {
      getPendingFiles(dropzone) === 0 &&
      dropzone.getFilesWithStatus(Dropzone.ERROR).length === 0
        ? onSuccess?.(JSON.stringify(getFilesData(dropzone)))
        : onError?.();
    };

    dropzone.off('complete');
    dropzone.on('complete', async (file: ExtendedDropzoneFile) => {
      if (file.status === Dropzone.SUCCESS) {
        const parts = file.upload?.chunks.map((chunk) => {
          const matcher = chunk.responseHeaders.match(/etag: "([^"]+)"/i);
          return {
            PartNumber: chunk.index + 1,
            ETag: matcher?.[1] ?? '',
          };
        });
        if (parts !== undefined) {
          await s3UploadHelper.completeMultipartUpload(
            file.fileId ?? '',
            parts
          );
        }
      }
      validateSucceededFiles();
    });
  }

  return {
    dropzone,
    files: localFiles,
    maxAllowedBulkUploadedFiles,
    isLoading: isLoadingMasterOrganization,
  };
}
